import VacancySettings from '@/orm/models/Vacancy/VacancySettings';
import BaseModel from '../BaseModel';
import VacancyContactPerson from './VacancyContactPerson';
import Address from '../Address/Address';
import VacancyFaq from './VacancyFaq';
import { vacancyStatus } from '../../../enums';
import Candidate from '../Candidate/Candidate';

export default class Vacancy extends BaseModel {
  static entity = 'vacancies';

  static primaryKey = 'id';

  static fields() {
    return {
      id: this.attr(null),
      companyId: this.attr(null),
      addressId: this.attr(null),
      completed: this.attr(null),
      status: this.string(null).nullable(),
      title: this.string(null).nullable(),
      description: this.string(null).nullable(),
      createdAt: this.string(null).nullable(),
      publishedFrom: this.string(null).nullable(),
      publishedTill: this.string(null).nullable(),
      archivedAt: this.string(null).nullable(),
      skills: this.attr([]),
      salary: this.attr(null),
      experience: this.attr(null).nullable(),
      schedule: this.attr([]),
      employment: this.attr([]),
      visibility: this.string(null).nullable(),
      candidates: this.hasMany(Candidate, 'vacancyId'),
      contactPerson: this.hasOne(VacancyContactPerson, 'vacancyId'),
      address: this.belongsTo(Address, 'addressId'),
      script: this.attr(null).nullable(),
      faq: this.hasMany(VacancyFaq, 'vacancyId'),
      counts: this.attr({
        candidates: 0,
        interviews: 0,
        interviewsNew: 0,
      }),
      wizardState: this.attr({}),
      externalVacancies: this.attr(null),
      settings: this.hasOne(VacancySettings, 'vacancyId'),
      link: this.attr(''),
      creatorName: this.attr(null),
      creator: this.attr(null),
      isAiInterviewCompiled: this.attr(null),
      isAiInterviewCompiling: this.attr(null),
      hasAiQuestions: this.attr(null),
      aiQuestionsGenerated: this.attr(null),
      aiQuestionsGenerating: this.attr(null),
      isAiInterviewRecompiling: this.attr(null),
      hasTest: this.attr(null),
      hasRedactingTest: this.attr(null),
      publishAfterAiCompile: this.attr(null),
      candidateAdditionalFields: this.attr(null),
      avatar: this.string(null).nullable(),
      aiInterviewPresetId: this.attr(null),
      hasHhIntegration: this.boolean(false),
    };
  }

  get userCreatorId() {
    return this.creator?.id;
  }

  static scopes = {
    archivedVacancies: () => this.query()
      .where('status', (s) => s === vacancyStatus.ARCHIVE),
    notArchivedVacancies: () => this.query()
      .where('status', (s) => s !== vacancyStatus.ARCHIVE),
    notArchivedNotDraftVacancies: () => this.query()
      .where('status', (s) => s !== vacancyStatus.ARCHIVE && s !== vacancyStatus.DRAFT),
    activeVacancies: () => this.query().where(vacancyStatus.ACTIVE === 'status'),
  };

  static apiConfig = {
    actions: {
      fetchAllByCompanyId(id) {
        return this.get(`companies/${id}/vacancies`, {
          dataTransformer: (r) => r.data.data.map((v) => ({ ...v, companyId: id })),
        });
      },
      fetchByCompanyIdAndUsers(id, payload) {
        return this.post(`companies/${id}/vacancies/by_users`, payload, {
          dataTransformer: (r) => r.data.data.map((v) => ({ ...v, companyId: id })),
        });
      },
      fetchById(id) {
        return this.get(`vacancies/${id}`);
      },
      store(companyId, payload) {
        return this.post(`companies/${companyId}/vacancies`, payload);
      },
      async update(id, payload) {
        return this.put(`vacancies/${id}`, payload);
      },
      changeStatus(id, status) {
        return this.put(`vacancies/${id}/status`, { newStatus: status });
      },
      changeWizardState(id, state) {
        return this.put(`vacancies/${id}`, { wizardState: state });
      },
      activate(id) {
        return this.changeStatus(id, vacancyStatus.ACTIVE);
      },
      archive(id) {
        return this.changeStatus(id, vacancyStatus.ARCHIVE);
      },
      restore(id) {
        return this.changeStatus(id, vacancyStatus.ACTIVE);
      },
      destroy(id) {
        return this.delete(`vacancies/${id}`);
      },
      clone(id) {
        return this.post(`vacancies/${id}/clone`);
      },
      cloneScript(destinationId, sourceId) {
        return this.post(`vacancies/${destinationId}/clone-script`, { source: sourceId });
      },
      updateSettings(id, payload) {
        return this.put(`vacancies/${id}/settings`, payload);
      },
      copyCandidates(id, payload) {
        return this.post(`vacancies/${id}/copy_candidates`, payload);
      },
      publishAfterAiCompile(id) {
        return this.put(`vacancies/${id}/publish_after_ai_compile`);
      },
    },
  }
}
